import {
  AsyncValidationError,
  BookmarkablePage,
  FormValues,
  getValue,
  InputWizardPanel,
  InputWizardPanelComponent,
  ReviewItem,
  ReviewSection,
  SelectOption,
  useNavigation,
} from "o4a-react";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { Stack } from "@fluentui/react";
import { MultiCloudDatabaseApiVersion } from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { DeploymentAsyncValidationPhase } from "../../components/DeploymentPhases/DeploymentAsyncValidationPhase";
import { DeployedService, DeploymentCreatePhase } from "../../components/DeploymentPhases/DeploymentCreationPhase";
import { ConsoleContext } from "../../console/ConsoleContext";
import {
  ADBS_CREATE_ROUTE,
  APEX_CREATE_ROUTE,
  AUTONOMOUS_DATABASE_SHARED_RESOURCE_TYPE_PATH,
  PageId,
  PageRegistrationConfig,
} from "../../constants/pluginConstants";
import { CreateWizardTestIds } from "../../constants/uiConstants";
import {
  AdbsDeploymentParametersDetails,
  CreateAdbsDatabaseDeploymentDetails,
} from "../../gen/clients/mchub-azure-api-client";
import {
  ACCESS_TYPES,
  AdbWorkloadType,
  DeploymentKinds,
  getResourceTypeMessage,
  getTagsPerResourceTypeMap,
  ResourceType,
} from "../../helpers/resourceHelper";
import { getTimestamp } from "../../helpers/timeHelper";
import { NavigationAnalyticsData, useAnalytics } from "../../hooks/useAnalytics";
import { useFeatures } from "../../hooks/useFeatures";
import {
  ADBS_DEPLOYMENT_CREATE_POLL_DELAY,
  ADBS_DEPLOYMENT_CREATE_POLL_INTERVAL,
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
} from "../../models/AsyncNotificationProviders";
import { ReviewTabContent } from "../CreateCommon/ReviewTabContent";
import { Fields as TagsFields, GROUP_TAGS, TagsTabContent } from "../CreateCommon/TagsTabContent";
import { BasicsTabContent, Fields as BasicsFields, GROUP_BASICS } from "./BasicsTabContent";
import { ConfigTabContent, Fields as ConfigFields, GROUP_CONFIG } from "./ConfigTabContent";
import { Fields as NetworkFields, GROUP_NETWORK, NetworkTabContent } from "./NetworkTabContent";
import { Fields as SecurityFields, GROUP_SECURITY, SecurityTabContent } from "./SecurityTabContent";

const registrationIds = PageRegistrationConfig[PageId.ADBS_CREATE].map(config => config.key);

enum CreationPhase {
  ASYNC_VALIDATE = "ASYNC_VALIDATE",
  CREATE_SUBMIT = "CREATE_SUBMIT",
}

export enum PanelTestIds {
  Basics = "basic",
  Configuration = "configuration",
  Network = "network",
  Security = "security",
  Tags = "tags",
}

export const AdbsCreatePage = (): JSX.Element => {
  const { pathname } = useLocation();
  const isApex = pathname.indexOf(APEX_CREATE_ROUTE) >= 0;

  const { trackActionDiscard } = useAnalytics();

  const { back, navigateToSelf, customData } = useNavigation(ConsoleContext);
  const { enableAdbsPE, enableAdbsPreflight } = useFeatures();
  const [subscriptionId, setSubscriptionId] = React.useState<string>("");
  const [resourceGroupName, setResourceGroupName] = React.useState<string>("");
  const [location, setLocation] = React.useState<string>("");
  const [workload, setWorkload] = React.useState<AdbWorkloadType>();

  const resourceNameRef = React.useRef<string>("");
  const deploymentNameRef = React.useRef<string>(""); // to hold the name to be used in failure message

  const [creationPhase, setCreationPhase] = React.useState<CreationPhase | undefined>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [createAdbsPayload, setCreateAdbsPayload] = React.useState<any>();
  const asyncValidationCallbacks = React.useRef<{
    resolve:(errors?: AsyncValidationError[]) => void,
    reject: (errorMessage: string) => void,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      }>({ resolve: () => {}, reject: () => {} });

  const submitCallbacks = React.useRef<{
    reject:(errorMessage: string, validationErrors?: AsyncValidationError[]) => void
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      }>({ reject: () => {} });

  React.useEffect(() => {
    // In case page was navigated to directly by entring its URL in the browser
    navigateToSelf(ADBS_CREATE_ROUTE, registrationIds[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [panelRef, setPanelRef] = React.useState<InputWizardPanelComponent>({} as InputWizardPanelComponent);

  const onAsyncValidate = (
    formValues: FormValues,
    resolve: (errors?: AsyncValidationError[]) => void,
    reject: (errorMessage: string) => void,
  ): void => {
    setCreateAdbsPayload(buildCreateAdbsPayload(formValues));
    asyncValidationCallbacks.current = { resolve, reject };
    setCreationPhase(CreationPhase.ASYNC_VALIDATE);
  };

  const onSubmit = (formValues: FormValues): void => {
    setCreateAdbsPayload(buildCreateAdbsPayload(formValues));
    setCreationPhase(CreationPhase.CREATE_SUBMIT);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const buildCreateAdbsPayload = (formValues: FormValues): any => {
    const tags = getValue(formValues, TagsFields.tags, GROUP_TAGS);
    const tagsMap = getTagsPerResourceTypeMap(tags);

    const name = getValue(formValues, BasicsFields.Name, GROUP_BASICS);
    resourceNameRef.current = name;

    let whitelistedIps: string[] | undefined;
    const accessType = getValue(formValues, NetworkFields.AccessType, GROUP_NETWORK)?.id;
    const isAccessControlEnabled = accessType === ACCESS_TYPES.RESTRICTED;
    const isAccessControlDisabled = accessType === ACCESS_TYPES.OPEN;
    if (isAccessControlEnabled) {
      whitelistedIps = getValue(formValues, NetworkFields.IPsCidrs, GROUP_NETWORK)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((ele: any) => ele.address !== undefined)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((ele: any) => ele.address);
    }
    const isRouteVNETChecked = getValue(formValues, NetworkFields.RouteVNET, GROUP_NETWORK);
    const selectedWorkload = getValue(formValues, ConfigFields.WorkloadType, GROUP_CONFIG)?.[0]?.id;
    const adbsDatabaseDetails: CreateAdbsDatabaseDeploymentDetails = {
      kind: DeploymentKinds.Create,
      name,
      source: "NONE",
      whitelistedIps,
      isAccessControlEnabled,
      freeformTags: tagsMap[ResourceType.AUTONOMOUS_DATABASE],
      dbName: getValue(formValues, ConfigFields.DatabaseName, GROUP_CONFIG),
      cpuCoreCount: getValue(formValues, ConfigFields.CoreCount, GROUP_CONFIG),
      dbWorkload: isApex ? AdbWorkloadType.APEX : selectedWorkload,
      dbVersion: getValue(formValues, ConfigFields.DbVersion, GROUP_CONFIG)?.[0].id,
      dataStorageSizeInTBs: getValue(formValues, ConfigFields.StorageSize, GROUP_CONFIG),
      licenseModel: isApex
        ? "LICENSE_INCLUDED"
        : getValue(formValues, ConfigFields.LicenseType, GROUP_CONFIG)?.[0].id,
      isAutoScalingEnabled: getValue(formValues, ConfigFields.AutoScaling, GROUP_CONFIG),
      adminPassword: getValue(formValues, SecurityFields.password, GROUP_SECURITY),
      isMtlsConnectionRequired: !isApex && selectedWorkload !== AdbWorkloadType.APEX
        ? getValue(formValues, NetworkFields.MTLSAuthentication, GROUP_NETWORK) : undefined,
      vnetId: (isAccessControlDisabled && isRouteVNETChecked) && enableAdbsPE
        ? getValue(formValues, NetworkFields.VNET, GROUP_NETWORK)?.[0].id : undefined,
      subnetId: (isAccessControlDisabled && isRouteVNETChecked) && enableAdbsPE
        ? getValue(formValues, NetworkFields.Subnet, GROUP_NETWORK)?.[0].id : undefined,
    };
    deploymentNameRef.current = `${AUTONOMOUS_DATABASE_SHARED_RESOURCE_TYPE_PATH}-${getTimestamp()}`;
    const createDeploymentDetails = {
      name: deploymentNameRef.current,
      parameters: {
        kind: DeploymentKinds.Adbs,
        adbsDatabaseDetails,
      } as AdbsDeploymentParametersDetails,
    };

    return {
      subscriptionId: getValue(formValues, BasicsFields.Subscription, GROUP_BASICS)?.[0].id,
      resourceGroupName: getValue(formValues, BasicsFields.ResourceGroup, GROUP_BASICS)?.[0].text,
      apiVersion: MultiCloudDatabaseApiVersion,
      createDeploymentDetails,
    };
  };

  const buildBasicInfo = (formValues: FormValues): ReviewItem[] => {
    const reviewBasicInfo: ReviewItem[] = [
      {
        label: Messages.labels.subscription(),
        value: getValue(formValues, BasicsFields.Subscription, GROUP_BASICS)?.[0].text,
      },
      {
        label: Messages.labels.resourceGroup(),
        value: getValue(formValues, BasicsFields.ResourceGroup, GROUP_BASICS)?.[0].text,
      },
      {
        label: Messages.labels.name(),
        value: getValue(formValues, BasicsFields.Name, GROUP_BASICS),
      },
      {
        label: Messages.labels.region(),
        value: getValue(formValues, BasicsFields.Region, GROUP_BASICS)?.[0].text,
      },
    ];
    return reviewBasicInfo;
  };
  const buildConfigurationInfo = (formValues: FormValues): ReviewItem[] => {
    const reviewConfigInfo: ReviewItem[] = [
      ...(!isApex ? [{
        label: Messages.labels.workloadType(),
        value: getValue(formValues, ConfigFields.WorkloadType, GROUP_CONFIG)?.[0].text,
      }] : []
      ),
      {
        label: Messages.labels.ocpuCount(),
        value: getValue(formValues, ConfigFields.CoreCount, GROUP_CONFIG),
      },
      {
        label: Messages.labels.ocpuAutoScaling(),
        value: getValue(formValues, ConfigFields.AutoScaling, GROUP_CONFIG).toString(),
      },
      {
        label: Messages.labels.storageInTB(),
        value: getValue(formValues, ConfigFields.StorageSize, GROUP_CONFIG),
      },
      ...(!isApex ? [{
        label: Messages.labels.licenseType(),
        value: getValue(formValues, ConfigFields.LicenseType, GROUP_CONFIG)?.[0].text,
      }] : []
      ),
      {
        label: Messages.labels.databaseVersion(),
        value: getValue(formValues, ConfigFields.DbVersion, GROUP_CONFIG)?.[0].text,
      },
      {
        label: Messages.labels.databaseName(),
        value: getValue(formValues, ConfigFields.DatabaseName, GROUP_CONFIG),
      },
    ];
    return reviewConfigInfo;
  };
  const buildNetworkingInfo = (formValues: FormValues): ReviewItem[] => {
    const addressesInfo: Record<NetworkFields.Address | NetworkFields.AddressRange, string>[] | undefined = getValue(
      formValues,
      NetworkFields.IPsCidrs,
      GROUP_NETWORK,
    );
    const accessType = getValue(formValues, NetworkFields.AccessType, GROUP_NETWORK);
    const isRouteVNET = getValue(formValues, NetworkFields.RouteVNET, GROUP_NETWORK);
    const selectedWorkload = getValue(formValues, ConfigFields.WorkloadType, GROUP_CONFIG)?.[0]?.id;
    const renderIPsCidrs = (
      values: Record<NetworkFields.Address | NetworkFields.AddressRange, string>[] | undefined,
    ): JSX.Element => {
      const cidrs = values?.map(value => {
        if (value && value?.[NetworkFields.Address]) {
          return (
            <Stack horizontal key={value?.[NetworkFields.Address]}>
              <span style={{ paddingBottom: 1 }}>
                {`${value?.[NetworkFields.Address]} [${value?.[NetworkFields.AddressRange]}]`}
              </span>
            </Stack>
          );
        }
        return "";
      });
      return (
        <div>
          {cidrs}
        </div>
      );
    };
    const reviewNetworkingInfo: ReviewItem[] = [
      {
        label: Messages.labels.accessType(),
        value: accessType.text,
      }];
    if (accessType?.id === ACCESS_TYPES.RESTRICTED) {
      reviewNetworkingInfo.push({
        label: Messages.labels.ipsAndOrCidrs(),
        value: renderIPsCidrs(addressesInfo),
      });
    }
    if (accessType?.id === ACCESS_TYPES.OPEN && enableAdbsPE) {
      reviewNetworkingInfo.push(
        {
          label: Messages.labels.routeVNET(),
          value: isRouteVNET.toString(),
        },
      );
      if (isRouteVNET) {
        reviewNetworkingInfo.push(
          {
            label: Messages.labels.virtualNetwork(),
            value: getValue(formValues, NetworkFields.VNET, GROUP_NETWORK)?.[0].text,
          },
          {
            label: Messages.labels.subnet(),
            value: getValue(formValues, NetworkFields.Subnet, GROUP_NETWORK)?.[0].text,
          },
        );
      }
    }
    if (!isApex && selectedWorkload !== AdbWorkloadType.APEX) {
      reviewNetworkingInfo.push({
        label: Messages.labels.requiretMTLSAuthentication(),
        value: getValue(formValues, NetworkFields.MTLSAuthentication, GROUP_NETWORK).toString(),
      });
    }

    return reviewNetworkingInfo;
  };
  const buildSecurityInfo = (formValues: FormValues): ReviewItem[] => {
    const reviewSecurityInfo: ReviewItem[] = [
      {
        label: Messages.labels.username(),
        value: getValue(formValues, SecurityFields.username, GROUP_SECURITY),
      },
    ];
    return reviewSecurityInfo;
  };

  const buildTagsInfo = (formValues: FormValues): ReviewItem[] => {
    const tagsInfo = getValue(formValues, TagsFields.tags, GROUP_TAGS);
    const tags: { label: string; value: string; }[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tagsInfo.forEach((element: any) => (
      element.resources.forEach((resource: { [key: string]: string }) => (
        element.name !== undefined && tags.push({
          label: element.name,
          value: `${element.value !== undefined ? element.value : ""}(${resource.text})`,
        })
      ))));
    if (tags.length === 0) tags.push({ label: "None", value: "" });
    tags.sort((a, b) => ((a.value.split("(")[1].split(")")[0] > b.value.split("(")[1].split(")")[0]) ? 1 : -1));
    return tags;
  };

  const onRenderReview = (formValues: FormValues): JSX.Element => {
    const reviewSections = [
      { title: Messages.createAdbs.basicsTab.title(), items: buildBasicInfo(formValues) },
      { title: Messages.createAdbs.configurationTab.title(), items: buildConfigurationInfo(formValues) },
      { title: Messages.createAdbs.networkingTab.title(), items: buildNetworkingInfo(formValues) },
      { title: Messages.createAdbs.securityTab.title(), items: buildSecurityInfo(formValues) },
      { title: Messages.tags.title(), items: buildTagsInfo(formValues) },
    ] as ReviewSection[];
    return <ReviewTabContent reviewSections={reviewSections} />;
  };

  const getTagsResourceTypeOptions = (): SelectOption[] => {
    const resourceOptions: SelectOption[] = [
      isApex
        ? { id: ResourceType.APEX, text: getResourceTypeMessage(ResourceType.APEX) }
        : { id: ResourceType.AUTONOMOUS_DATABASE, text: getResourceTypeMessage(ResourceType.AUTONOMOUS_DATABASE) },
    ];
    return resourceOptions;
  };

  const longTitle = isApex ? Messages.createApex.titles.long() : Messages.createAdbs.titles.long();
  const shortTitle = isApex ? Messages.createApex.titles.short() : Messages.createAdbs.titles.short();
  return (
    <BookmarkablePage
      appContext={ConsoleContext}
      registrationIds={registrationIds}
      title={shortTitle}
    >
      <InputWizardPanel
        componentRef={setPanelRef}
        title={longTitle}
        testId={CreateWizardTestIds.Adbs}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onFormChange={(_formValues: FormValues, fieldValue: any, field: string) => {
          if (field === BasicsFields.Subscription) {
            setSubscriptionId(fieldValue?.[0]?.id);
          } else if (field === BasicsFields.ResourceGroup) {
            setResourceGroupName(fieldValue?.[0]?.text);
          } else if (field === BasicsFields.Region) {
            setLocation(fieldValue?.[0]?.id);
          } else if (field === ConfigFields.WorkloadType) {
            setWorkload(fieldValue?.[0]?.id);
          }
        }}
        items={[
          {
            header: Messages.createAdbs.basicsTab.title(),
            groupName: GROUP_BASICS,
            testId: PanelTestIds.Basics,
            content: (
              <BasicsTabContent
                location={location}
                subscription={subscriptionId}
                inputWizardPanelRef={panelRef}
              />
            ),
          },
          {
            header: Messages.createAdbs.configurationTab.title(),
            groupName: GROUP_CONFIG,
            testId: PanelTestIds.Configuration,
            content: (
              <ConfigTabContent
                subscription={subscriptionId}
                location={location}
                inputWizardPanelRef={panelRef}
                isApex={isApex}
              />
            ),
          },
          {
            header: Messages.createAdbs.networkingTab.title(),
            groupName: GROUP_NETWORK,
            testId: PanelTestIds.Network,
            content: (
              <NetworkTabContent
                subscription={subscriptionId}
                location={location}
                inputWizardPanelRef={panelRef}
                workload={workload}
                isApex={isApex}
              />
            ),
          },
          {
            header: Messages.createAdbs.securityTab.title(),
            groupName: GROUP_SECURITY,
            testId: PanelTestIds.Security,
            content: <SecurityTabContent />,
          },
          {
            header: Messages.tags.title(),
            groupName: GROUP_TAGS,
            testId: PanelTestIds.Tags,
            content: <TagsTabContent resourceOptions={getTagsResourceTypeOptions()} />,
          },
        ]}
        onRenderReview={onRenderReview}
        onUnselectingReview={() => {
          setCreateAdbsPayload(undefined);
          setCreationPhase(undefined);
        }}
        onAsyncValidate={enableAdbsPreflight ? onAsyncValidate : undefined}
        onSubmit={(
          formValues: FormValues,
          reject: (errorMessage: string, validationErrors?: AsyncValidationError[]) => void,
        ) => {
          submitCallbacks.current = { reject };
          onSubmit(formValues);
        }}
        onClose={() => {
          const analytics = customData?.analytics as NavigationAnalyticsData;
          if (analytics) {
            trackActionDiscard(analytics.actionName, analytics.pageId, analytics.panelId);
          }
          back();
        }}
      />
      {creationPhase === CreationPhase.CREATE_SUBMIT && (
        <DeploymentCreatePhase
          panelRef={panelRef}
          location={location}
          deployedService={isApex ? DeployedService.APEX : DeployedService.AUTONOMOUS_SHARED_DATABASE}
          resourceName={resourceNameRef.current}
          deploymentName={deploymentNameRef.current}
          resourceGroupName={resourceGroupName}
          subscriptionId={subscriptionId}
          submitPayload={createAdbsPayload}
          submitReject={submitCallbacks.current.reject}
          asyncNotification={{
            methodKey: AsyncNotificationMethodKey.DEPLOYMENT_GET,
            delay: ADBS_DEPLOYMENT_CREATE_POLL_DELAY,
            pollingInterval: ADBS_DEPLOYMENT_CREATE_POLL_INTERVAL,
            polledResponseKey: AsyncNotificationPolledResponseKey.ADBS_DEPLOYMENT_CREATED_CHECK,
          }}
          onPostSubmit={() => {
            setCreateAdbsPayload(undefined);
            setCreationPhase(undefined);
          }}
        />
      )}
      {creationPhase === CreationPhase.ASYNC_VALIDATE && (
        <DeploymentAsyncValidationPhase
          location={location}
          asyncValidatePayload={createAdbsPayload}
          asyncValidateResolve={asyncValidationCallbacks.current.resolve}
          asyncValidateReject={asyncValidationCallbacks.current.reject}
          onPostAsyncValidate={() => {
            setCreateAdbsPayload(undefined);
            setCreationPhase(undefined);
          }}
        />
      )}
    </BookmarkablePage>
  );
};
